import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Image,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  ChevronLeft,
  ChevronRight,
  Circle,
  Eye,
  Filter,
  Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SessionCategoryCell } from '../../../pages/sessions/components/SessionCategoryCell';
import type { PatientSessionListItem, PatientSessionsSummary } from '../types';

interface PatientSessionsTabProps {
  sessions: PatientSessionListItem[];
  summary: PatientSessionsSummary;
}

const columnHelper = createColumnHelper<PatientSessionListItem>();

const StatusBadgeWithDot = memo<{ status: string }>(({ status }) => {
  const config = useMemo(() => {
    switch (status) {
      case 'in-progress':
        return {
          color: 'blue' as const,
          text: 'In corso',
          dotColor: 'var(--mantine-color-blue-6)',
        };
      case 'queued':
        return {
          color: 'orange' as const,
          text: 'Programmata',
          dotColor: 'var(--mantine-color-orange-6)',
        };
      case 'completed':
        return {
          color: 'green' as const,
          text: 'Completata',
          dotColor: 'var(--mantine-color-green-6)',
        };
      case 'initializing':
        return {
          color: 'grape' as const,
          text: 'Inizializzazione',
          dotColor: 'var(--mantine-color-grape-6)',
        };
      default:
        return {
          color: 'gray' as const,
          text: status || 'Sconosciuto',
          dotColor: 'var(--mantine-color-gray-6)',
        };
    }
  }, [status]);

  return (
    <Badge
      color={config.color}
      variant='light'
      size='sm'
      leftSection={
        <Circle size={6} fill={config.dotColor} color={config.dotColor} />
      }
    >
      {config.text}
    </Badge>
  );
});

StatusBadgeWithDot.displayName = 'StatusBadgeWithDot';

const SessionsPagination = memo<{
  totalRecords: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}>(({ totalRecords, page, pageSize, onPageChange, onPageSizeChange }) => {
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / pageSize)),
    [totalRecords, pageSize]
  );

  const startRecord = useMemo(
    () => (totalRecords === 0 ? 0 : (page - 1) * pageSize + 1),
    [page, pageSize, totalRecords]
  );

  const endRecord = useMemo(
    () => Math.min(page * pageSize, totalRecords),
    [page, pageSize, totalRecords]
  );

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }

    return pages;
  }, [page, totalPages]);

  return (
    <Flex justify='space-between' align='center' mt='md' wrap='wrap' gap='md'>
      <Group gap='sm' align='center'>
        <Text size='sm' c='dimmed'>
          Mostrate {startRecord}-{endRecord} sessioni su {totalRecords}
        </Text>
        <Select
          value={pageSize.toString()}
          onChange={value => {
            if (!value) return;
            onPageSizeChange(Number(value));
            onPageChange(1);
          }}
          data={[
            { value: '10', label: '10' },
            { value: '20', label: '20' },
            { value: '50', label: '50' },
            { value: '100', label: '100' },
          ]}
          size='sm'
          w={80}
          radius='sm'
        />
      </Group>
      <Group gap='xs'>
        <Button
          variant='outline'
          size='sm'
          radius='sm'
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          leftSection={<ChevronLeft size={16} />}
        >
          Previous
        </Button>
        {pageNumbers.map((pageNum, index) =>
          pageNum === '...' ? (
            <Text key={`ellipsis-${index}`} size='sm' c='dimmed' px='xs'>
              ...
            </Text>
          ) : (
            <Button
              key={pageNum}
              variant={pageNum === page ? 'filled' : 'subtle'}
              color={pageNum === page ? 'blue' : 'gray'}
              size='sm'
              radius='sm'
              onClick={() => onPageChange(pageNum as number)}
            >
              {pageNum}
            </Button>
          )
        )}
        <Button
          variant='filled'
          color='blue'
          size='sm'
          radius='sm'
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          rightSection={<ChevronRight size={16} />}
        >
          Avanti
        </Button>
      </Group>
    </Flex>
  );
});

SessionsPagination.displayName = 'SessionsPagination';

export function PatientSessionsTab({
  sessions,
  summary,
}: PatientSessionsTabProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>('all');
  const [domainFilter, setDomainFilter] = useState<string | null>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const domainOptions = useMemo(
    () => [
      { value: 'all', label: 'Tutti i domini' },
      ...summary.domains.map(domain => ({
        value: domain.domainKey,
        label: `${domain.label} (${domain.count})`,
      })),
    ],
    [summary.domains]
  );

  const hasActiveFilters =
    searchValue.trim().length > 0 ||
    statusFilter !== 'all' ||
    domainFilter !== 'all';

  const filteredSessions = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return sessions.filter(session => {
      const matchesStatus =
        statusFilter == null || statusFilter === 'all'
          ? true
          : session.status === statusFilter;
      const matchesDomain =
        domainFilter == null || domainFilter === 'all'
          ? true
          : session.domainKey === domainFilter;
      const matchesSearch =
        normalizedSearch.length === 0
          ? true
          : [
              String(session.id),
              session.type,
              session.domainLabel,
              session.scenario ?? '',
              session.deviceName ?? '',
              session.deviceId ?? '',
              session.patientCode ?? '',
            ]
              .join(' ')
              .toLowerCase()
              .includes(normalizedSearch);

      return matchesStatus && matchesDomain && matchesSearch;
    });
  }, [domainFilter, searchValue, sessions, statusFilter]);

  const tableData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredSessions.slice(startIndex, startIndex + pageSize);
  }, [filteredSessions, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [domainFilter, searchValue, statusFilter]);

  const handleResetFilters = () => {
    setSearchValue('');
    setStatusFilter('all');
    setDomainFilter('all');
  };

  const handleDomainStatClick = (domainKey: string) => {
    setDomainFilter(current => (current === domainKey ? 'all' : domainKey));
  };

  const columns = useMemo<ColumnDef<PatientSessionListItem>[]>(() => {
    const baseColumns: ColumnDef<PatientSessionListItem>[] = [
      columnHelper.display({
        id: 'status',
        header: 'Stato',
        minSize: 120,
        size: 120,
        cell: ({ row }) => <StatusBadgeWithDot status={row.original.status} />,
      }),
      columnHelper.display({
        id: 'sessionId',
        header: 'ID sessione',
        cell: ({ row }) => (
          <Text size='sm' fw={500}>
            #{row.original.id}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'category',
        header: 'Dominio',
        cell: ({ row }) => (
          <SessionCategoryCell
            type={row.original.type}
            scenario={row.original.scenario}
            source={row.original.experienceSource}
          />
        ),
      }),
      columnHelper.display({
        id: 'device',
        header: 'Dispositivo',
        cell: ({ row }) => {
          const session = row.original;
          return (
            <Stack gap={2}>
              <Text size='sm' fw={500}>
                {session.deviceName ?? 'N/D'}
              </Text>
              {session.deviceId ? (
                <Text size='xs' c='dimmed'>
                  ID: {session.deviceId}
                </Text>
              ) : null}
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: 'date',
        header: 'Ultimo aggiornamento',
        minSize: 180,
        size: 180,
        cell: ({ row }) => {
          const session = row.original;
          const updatedDate = session.updatedAt
            ? new Date(session.updatedAt)
            : session.createdAt
              ? new Date(session.createdAt)
              : null;

          if (updatedDate == null || Number.isNaN(updatedDate.getTime())) {
            return (
              <Text size='sm' fw={500}>
                —
              </Text>
            );
          }

          return (
            <Stack gap={2}>
              <Text size='sm' fw={500}>
                {formatDate(updatedDate)} · {formatTime(updatedDate)}
              </Text>
              {session.createdAt && session.updatedAt ? (
                <Text size='xs' c='dimmed'>
                  Aggiornata
                </Text>
              ) : null}
            </Stack>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Azioni',
        minSize: 100,
        size: 100,
        cell: ({ row }) => (
          <Button
            variant='subtle'
            size='sm'
            leftSection={<Eye size={16} />}
            onClick={() => navigate(`/sessions/${row.original.id}`)}
          >
            Apri
          </Button>
        ),
      }),
    ];

    if (isMobile) {
      return baseColumns.filter(
        column =>
          column.id === 'status' ||
          column.id === 'category' ||
          column.id === 'actions'
      );
    }

    if (isTablet) {
      return baseColumns.filter(column => column.id !== 'device');
    }

    return baseColumns;
  }, [formatDate, formatTime, isMobile, isTablet, navigate]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Stack gap='lg'>
      <Card shadow='sm' withBorder radius='sm' p='md'>
        <Stack gap='sm'>
          <Group
            justify='space-between'
            align='flex-start'
            wrap='wrap'
            gap='sm'
          >
            <Stack gap={2}>
              <Text size='sm' fw={600}>
                Dominio sessioni
              </Text>
              <Text size='xs' c='dimmed'>
                Distribuzione per tipologia: Body Image, Exposure, Experience e
                altre categorie mappate.
              </Text>
            </Stack>
            <Text size='sm' c='dimmed'>
              {summary.total} totali · {summary.inProgress + summary.queued}{' '}
              attive · {summary.completed} completate
            </Text>
          </Group>

          {summary.domains.length === 0 ? (
            <Text size='sm' c='dimmed'>
              Nessuna sessione collegata a questo utente.
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: 4 }} spacing='xs'>
              {summary.domains.map(domain => {
                const isActive = domainFilter === domain.domainKey;

                return (
                  <UnstyledButton
                    key={domain.domainKey}
                    onClick={() => handleDomainStatClick(domain.domainKey)}
                    aria-pressed={isActive}
                  >
                    <Paper
                      withBorder
                      p='sm'
                      radius='sm'
                      bg={isActive ? 'blue.0' : 'white'}
                      style={{
                        borderColor: isActive
                          ? 'var(--mantine-color-blue-5)'
                          : undefined,
                      }}
                    >
                      <Group
                        justify='space-between'
                        align='center'
                        wrap='nowrap'
                      >
                        <Group gap='xs' wrap='nowrap' miw={0}>
                          <Image
                            src={domain.iconUrl}
                            w={20}
                            h={20}
                            fit='contain'
                            alt=''
                          />
                          <Text size='sm' fw={500} truncate>
                            {domain.label}
                          </Text>
                        </Group>
                        <Badge
                          variant={isActive ? 'filled' : 'light'}
                          color={isActive ? 'blue' : 'gray'}
                          size='sm'
                        >
                          {domain.count}
                        </Badge>
                      </Group>
                    </Paper>
                  </UnstyledButton>
                );
              })}
            </SimpleGrid>
          )}
        </Stack>
      </Card>

      <Card shadow='sm' withBorder radius='sm' p='lg'>
        <Flex direction='column' gap='md'>
          <Flex justify='space-between' align='center' wrap='wrap' gap='md'>
            <Group gap='xs'>
              <Filter size={16} color='var(--mantine-color-gray-5)' />
              <Text size='sm' fw={500}>
                Sessioni utente
              </Text>
            </Group>
            <Text size='sm' c='dimmed'>
              {summary.total} totali · {summary.inProgress + summary.queued}{' '}
              attive · {summary.completed} completate
              {filteredSessions.length !== sessions.length
                ? ` · ${filteredSessions.length} filtrate`
                : ''}
            </Text>
          </Flex>

          <Flex
            align='flex-end'
            wrap='wrap'
            gap='md'
            direction={{ base: 'column', sm: 'row' }}
          >
            <Box flex={1} miw={240} w={{ base: '100%', sm: 'auto' }}>
              <TextInput
                label='Cerca sessione'
                placeholder='ID, app, device...'
                leftSection={
                  <Search size={16} color='var(--mantine-color-gray-6)' />
                }
                value={searchValue}
                onChange={event => setSearchValue(event.currentTarget.value)}
                radius='sm'
                size='sm'
              />
            </Box>
            <Group
              gap='xs'
              wrap='wrap'
              align='flex-end'
              w={{ base: '100%', sm: 'auto' }}
            >
              <Select
                label='Stato'
                data={[
                  { value: 'all', label: 'Tutti gli stati' },
                  { value: 'queued', label: 'Programmata' },
                  { value: 'in-progress', label: 'In corso' },
                  { value: 'completed', label: 'Completata' },
                ]}
                value={statusFilter}
                onChange={setStatusFilter}
                size='sm'
                radius='sm'
                w={{ base: '100%', xs: 180 }}
              />
              <Select
                label='Dominio'
                data={domainOptions}
                value={domainFilter}
                onChange={setDomainFilter}
                size='sm'
                radius='sm'
                w={{ base: '100%', xs: 220 }}
              />
              {hasActiveFilters ? (
                <Button variant='subtle' size='sm' onClick={handleResetFilters}>
                  Reimposta filtri
                </Button>
              ) : null}
            </Group>
          </Flex>
        </Flex>
      </Card>

      {hasActiveFilters ? (
        <Text size='sm' c='dimmed'>
          Mostrando {filteredSessions.length} session
          {filteredSessions.length !== 1 ? 'i' : 'e'} su {sessions.length}{' '}
          totali
        </Text>
      ) : null}

      <Card shadow='sm' radius='sm' withBorder p='md'>
        <Stack gap='md'>
          <Box>
            <Text size='sm' fw={600} mb={4}>
              Sessioni
            </Text>
            <Text size='xs' c='dimmed'>
              Visualizza e apri le sessioni collegate a questo utente
            </Text>
          </Box>

          {filteredSessions.length === 0 ? (
            <Box ta='center' py='xl'>
              <Stack gap='md' align='center'>
                <Text size='sm' c='dimmed'>
                  {hasActiveFilters
                    ? 'Nessuna sessione corrisponde ai filtri selezionati'
                    : 'Nessuna sessione collegata a questo utente'}
                </Text>
                {hasActiveFilters ? (
                  <Text size='xs' c='dimmed'>
                    Prova a cambiare la ricerca o i filtri selezionati.
                  </Text>
                ) : (
                  <Text size='xs' c='dimmed'>
                    Torna in overview per aprire altri domini.
                  </Text>
                )}
              </Stack>
            </Box>
          ) : (
            <>
              <Table
                verticalSpacing='xs'
                horizontalSpacing='sm'
                highlightOnHover
                withTableBorder
              >
                <Table.Thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <Table.Tr
                      key={headerGroup.id}
                      bg='var(--mantine-color-gray-0)'
                    >
                      {headerGroup.headers.map(header => (
                        <Table.Th key={header.id} p='sm' fw={500}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Thead>
                <Table.Tbody>
                  {table.getRowModel().rows.map(row => (
                    <Table.Tr key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <Table.Td key={cell.id} p='sm'>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <SessionsPagination
                totalRecords={filteredSessions.length}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
